<template>
	<UserRegistrationLayout :isGoogleSignupMode="isGoogleSignupMode" showProgressBar class="user-details-form" :isSlideUpAnimation="true">
		<div class="slot-title mb-4">Set up your account</div>
		<v-form v-model="isUserDetailsValid" ref="userDetailsForm" @submit.prevent="submitForm" class="form-section">
			<div class="flex-centerize" v-if="!isGoogleSignupMode" :class="{ 'flex-column width-100': isMobile }">
				<SignupTextField
					:class="{ 'mr-2': !isMobile }"
					class="width-100"
					label="First name"
					iconName="man"
					:rules="nameRules"
					v-model="userData.firstName"
					type="text"
					data-cy="registration-first-name"
					placeholder="John"
				>
				</SignupTextField>
				<SignupTextField
					:class="{ 'mr-2': !isMobile }"
					label="Last name"
					class="width-100"
					iconName="man"
					:rules="nameRules"
					v-model="userData.lastName"
					type="text"
					data-cy="registration-last-name"
					placeholder="Smith"
				>
				</SignupTextField>
			</div>
			<PasswordTextField
				v-if="!isGoogleSignupMode"
				label="Create password"
				v-model="userData.password"
				:password="userData.password"
				@password-valid-update="onPasswordValidationUpdate"
			/>
			<SignupTextField
				v-if="!uid"
				label="Company name"
				iconName="office-buildings"
				v-model="userDetails.companyName"
				:rules="requiredRules"
				placeholder="Your company name"
			/>
			<SignupSelectField
				v-if="isGoogleSignupMode && !uid"
				label="I’m a"
				iconName="star"
				:items="roleItems"
				v-model="userDetails.roleSelection"
				:rules="requiredRules"
				placeholder="Select role"
			/>
			<SignupSelectField
				v-if="!uid"
				label="Company size"
				iconName="briefcase"
				:items="companySizeItems"
				v-model="userDetails.companySize"
				:rules="requiredRules"
				placeholder="Select company size"
			/>
			<div class="agree-to-terms-section display-flex flex-row flex-align-center">
				<v-checkbox class="mt-n4" required hide-details single-line :rules="requiredRules" v-model="acceptPrivacy" />
				<div class="agree-to-terms">
					I agree to the <a class="link" @click.stop href="https://go.ply.io/Terms" target="_blank">terms of service</a>,
					<a href="https://info.ply.io/privacy" class="link" @click.stop target="_blank">privacy policy</a> and
					<a href="https://info.ply.io/data-processing-agreement" @click.stop class="link" target="_blank">data processing agreement</a>
				</div>
			</div>
			<AnemicButton :loading="submitting" class="signup-button mt-8 width-100" type="submit" data-cy="registration-continue">
				{{ uid ? 'Join Workspace' : 'Create account' }}
			</AnemicButton>

			<div v-if="showError" class="error-text">
				{{ errorMessage || `An error occurred, couldn't update your workspace settings. Please try again` }}
			</div>
		</v-form>
	</UserRegistrationLayout>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { mapActions } from 'vuex';

import UserRegistrationLayout from './UserRegistrationLayout.vue';
import PasswordTextField from './components/PasswordTextField';
import SignupSelectField from './components/SignupSelectField';
import SignupTextField from './components/SignupTextField';
import UserRegistrationMixin from './helpers/UserRegistrationMixin';
import UserRegistrationService from './helpers/UserRegistrationService';
import { COMPANY_ROLE, COMPANY_FUNCTION, COMPANY_SIZE } from './helpers/userRegistration.const';
import AnalyticsService from '@/utils/AnalyticsService';
import SignUpService from '@/utils/sign-up-service';

const isMobile = require('is-mobile');
const signupCode = 'LetsPly'; //TODO: Remove this from the server

export default {
	name: 'UserDetailsForm',
	components: {
		UserRegistrationLayout,
		SignupSelectField,
		SignupTextField,
		PasswordTextField,
	},
	mixins: [UserRegistrationMixin],
	props: {
		uid: { type: String },
		token: { type: String },
		isInstallable: { type: Boolean },
	},
	computed: {
		isGoogleSignupMode() {
			return Boolean(this.$route.params?.gtoken);
		},
		isMobile() {
			return isMobile();
		},
	},
	methods: {
		...mapActions(['saveUserMetadata', 'registerUser']),
		loadGtag() {
			AnalyticsService.loadGtag();
		},
		async submitForm() {
			this.$refs.userDetailsForm.validate();
			if (this.isUserDetailsValid) {
				this.showError = false;
				this.submitting = true;

				try {
					const payload = {
						...this.userData,
						signupCode,
						signupReferrer: this.signupReferrer,
						uid: this.uid,
						metadata: this.userDetails,
						timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
						gtoken: this.$route.params?.gtoken,
						isInstallable: this.isInstallable,
					};
					if (payload.gtoken) {
						delete payload['password'];
					}

					const isGoogleSignUp = Boolean(this.$route.params?.gtoken);

					const user = await this.registerUser(payload);
					SignUpService.clearPlyWebsiteCookieUtmParams(); // clear website cookie utm params

					const utms = this.userDetails['utms'] || {};
					delete this.userDetails['utms'];

					AnalyticsService.updateUserProps({ ...this.userDetails, ...utms });
					AnalyticsService.sendFBEvent('Account Created', {});

					try {
						window.gtag('event', 'conversion', { send_to: 'AW-10858211195/IssXCKmlwdAZEPvOzLko' }); // Google Ads conversion tracking
					} catch (e) {
						console.error('Google Ads conversion tracking failed', e);
					}

					// Twitter Ads conversion tracking
					try {
						twq('event', 'tw-o8rfq-onmox', {
							email_address: this.userData?.email, // use this to pass a user’s email address
						});
					} catch (e) {
						console.error('Twitter Ads conversion tracking failed', e);
					}

					const { signup_referrer } = user.profile?.metadata;
					const googleSignupNavigateTo = await UserRegistrationService.getNavigateToFirstPageObject(
						signup_referrer,
						this.$route.query.return_to
					);
					if (isGoogleSignUp || this.uid) {
						this.navigateTo(googleSignupNavigateTo);
					} else {
						this.navigateTo({ name: 'SignedUpSuccessfull', params: { userEmail: user.email } });
					}
				} catch (err) {
					console.error(err);
					this.errorMessage = err?.response?.data?.error?.msg;
					this.showError = true;
				}
				this.submitting = false;
				return;
			}
		},
		onPasswordValidationUpdate(newVal) {
			this.isPasswordValid = newVal;
		},
	},
	mounted() {
		// Load GTAG
		this.loadGtag();
	},
	created() {
		if (isEmpty(this.$route.params)) {
			this.navigateTo({ name: 'Signup' });
		}
		this.userData.email = this.$route.params.userEmail;
		if (this.$route.params.installableId) {
			this.userData.pending_installations = [this.$route.params.installableId];
			this.userDetails['installableId'] = this.$route.params.installableId;
		}
		/*
			const utms = SignUpService.getReferralAndUtmParams(this.$route.query);
			this.userDetails.utms = utms;
		*/

		try {
			const firstPageView = AnalyticsService.getFirstPageViewParams();
			if (firstPageView) {
				this.userDetails = { ...this.userDetails, ...firstPageView };
			}
		} catch (e) {
			console.log('Failed to get page views', e);
		}
	},

	data: () => ({
		errorMessage: '',
		showError: false,
		isUserDetailsValid: false,
		submitting: false,
		requiredRules: UserRegistrationService.getRequiredRules(),
		roleItems: COMPANY_ROLE,
		workItems: COMPANY_FUNCTION,
		companySizeItems: COMPANY_SIZE,
		userDetails: {
			roleSelection: null,
			workSelection: null,
			companyName: null,
			companySize: null,
			pending_installations: [],
		},
		userData: {
			firstName: '',
			lastName: '',
			email: '',
			password: '',
		},

		auth2: null,

		isPasswordValid: false,
		acceptPrivacy: false,
		isSignupFormValid: false,
		emailRules: UserRegistrationService.getEmailValidationRules(),
		passwordRules: UserRegistrationService.getPasswordValidationRules(),
		nameRules: UserRegistrationService.getNameRules(),
		suggestedEmail: null,
		suspiciousEmail: false,
		signupReferrer: UserRegistrationService.getSignupReferrer(document.referrer),
	}),
};
</script>

<style scoped lang="scss">
.user-details-form {
	.form-section {
		width: 400px;
		@media (max-width: 600px) {
			width: 100%;
		}
	}
	.input-field {
		label {
			margin: 6px 0 4px 0;
			font-size: 15px;
			font-weight: 300;
		}
	}
}
.error-text {
	color: red;
	font-size: 14px;
	margin-top: 20px;
}
.agree-to-terms-section {
	::v-deep .v-icon:not(.primary--text):not(.error--text) {
		font-size: 12px !important;
	}

	.agree-to-terms {
		font-weight: 300;
		color: #373737;
		text-align: left;
		font-size: 12px !important;
	}
}
.link {
	font-weight: 300;
	color: #373737 !important;
}
.signup-button {
	height: 45px;
	padding: 0px;
	font-size: 14px !important;
	color: var(--bg-color) !important;
	background: var(--bold-border-color) !important;
}
</style>
